import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Typography, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Checkbox,
  TextField, Button, FormControl, InputLabel, Select,
  MenuItem, Chip, IconButton, Grid,
  Alert, Snackbar, Dialog, DialogTitle, DialogContent, DialogActions, Stack, CircularProgress,
  LinearProgress, Paper
} from '@mui/material';
import {
  Edit,
  Save,
  Cancel,
  Update,
  CalendarToday,
  LocalHospital,
  Work,
  TrendingDown,
  Sort,
  PictureAsPdf,
  FileDownload
} from '@mui/icons-material';
import { getAllLeaveBalanceCombined, getAllLeaveBalance, updateLeaveBalance, updatePendingBalance, getMyLeaveBalance } from '../../services/leaveBalanceAPIs';
import LeaveBalance from '../../components/dash/BalanceOverview';
// import {transformLeaveBalances} from './helper.js';
import PageHeader from '../../components/headers/Header';

// Import libraries for export functionality
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const LeaveBalanceManager = ({ IsEmployee=false }) => {
  const [employees, setEmployees] = useState([]);
  const [selected, setSelected] = useState([]);
  const [editingRow, setEditingRow] = useState(null); // Tracks {rowId, year}
  const [editValues, setEditValues] = useState({});
  const [filterDepartment, setFilterDepartment] = useState('');
  const [filterPosition, setFilterPosition] = useState('');
  const [filterName, setFilterName] = useState('');
  const [filterZeroBalances, setFilterZeroBalances] = useState('all'); // 'all', 'only', 'exclude'
  const [filterPendingBalance, setFilterPendingBalance] = useState(false); // show only pendingBalance > 0
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const [bulkUpdateDialog, setBulkUpdateDialog] = useState(false);
  const [bulkValues, setBulkValues] = useState({ balance: '', leaveType: 'ANNUAL_LEAVE' });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [loading, setLoading] = useState(true);

  const leaveTypeLabels = {
    SICK_LEAVE: 'Sick Leave',
    ANNUAL_LEAVE: 'Annual Leave',
    UNPAID_LEAVE: 'Unpaid Leave',
  };

  const leaveTypeIcons = {
    ANNUAL_LEAVE: CalendarToday,
    SICK_LEAVE: LocalHospital,
    UNPAID_LEAVE: Work,
  };

  const leaveTypeColors = {
    ANNUAL_LEAVE: '#4CAF50',
    SICK_LEAVE: '#FF9800',
    UNPAID_LEAVE: '#9E9E9E',
  };

  // Default balances based on your backend logic
  const getDefaultBalance = (employee, position, department) => {
    if (employee.annualVacation) {
      return { ANNUAL_LEAVE: employee.annualVacation, SICK_LEAVE: 10, UNPAID_LEAVE: 5 };
    } else {
      return { ANNUAL_LEAVE: 0, SICK_LEAVE: 10, UNPAID_LEAVE: 3 };
    }
  };

  const currentYear = new Date().getFullYear();
  const previousYear = currentYear - 1;

  const calculateRealVacation = (joiningDate) => {
    if (!joiningDate) return 0;
    
    const joinDate = new Date(joiningDate);
    const today = new Date();
    const currentYear = today.getFullYear();
    
    let startDate;
    let isJoiningMonthThisYear = joinDate.getFullYear() === currentYear;
    
    if (!isJoiningMonthThisYear) {
      startDate = new Date(currentYear, 0, 1);
    } else {
      startDate = joinDate;
    }
    
    let totalMonths = (today.getFullYear() - startDate.getFullYear()) * 12;
    totalMonths -= startDate.getMonth();
    totalMonths += today.getMonth();
    totalMonths += 1;
    
    if (totalMonths <= 0) return 0;
    
    // Helper: prorate a month based on day (1st-15th = 1 day, 16th+ = 0.5 day)
    const prorateMonth = (day) => day <= 15 ? 1 : 0.5;
    
    if (!isJoiningMonthThisYear) {
      const currentMonthProrated = prorateMonth(today.getDate());
      if (totalMonths === 1) return currentMonthProrated;
      const fullMonths = totalMonths - 1;
      return (fullMonths * 1.5) + currentMonthProrated;
    }
    
    const joiningMonthCredit = prorateMonth(startDate.getDate());
    
    if (totalMonths === 1) return joiningMonthCredit;
    
    const currentMonthCredit = prorateMonth(today.getDate());
    
    if (totalMonths === 2) return joiningMonthCredit + currentMonthCredit;
    
    const fullMonths = totalMonths - 2;
    return joiningMonthCredit + (fullMonths * 1.5) + currentMonthCredit;
  };

  useEffect(() => {
    const fetchLeaveBalances = async () => {
      try {
        let data;
        if (IsEmployee) {
          data = await getMyLeaveBalance();
          console.log('Employee Leave Balance:', data);
          const transformedData = data.map(item => ({
            ...item,
            employee: data[0]?.employee || {},
            department: data[0]?.employee?.department?.name || '',
            position: data[0]?.employee?.position || '',
          }));
          setEmployees(transformedData);
        } else {
          data = await getAllLeaveBalanceCombined();
          console.log('Transformed Leave Balances:', data);
          setEmployees(data);
        }
      } catch (error) {
        console.error('Error fetching leave balances:', error);
        showSnackbar('Error loading leave balances', 'error');
      } finally {
        setLoading(false);
      }
    };
    fetchLeaveBalances();
  }, [IsEmployee]);

  // Get departments and positions from valid employee data
  const departments = [...new Set(employees
    .filter(emp => emp.department || emp.employee?.department?.name)
    .map(emp => emp.department || emp.employee?.department?.name))];
  
  const positions = [...new Set(employees
    .filter(emp => emp.position || emp.employee?.position)
    .map(emp => emp.position || emp.employee?.position))];
  
  const leaveTypes = ['ANNUAL_LEAVE'];

  const filteredEmployees = employees.filter(emp => {
    const firstName = emp.employee?.firstName || '';
    const lastName = emp.employee?.lastName || '';
    const fullName = `${firstName} ${lastName}`.toLowerCase();
    
    const empDepartment = emp.employee?.department?.name || '';
    const empPosition = emp.employee?.position || '';
    
    const matchesFilters = (
      (filterDepartment === '' || empDepartment === filterDepartment) &&
      (filterPosition === '' || empPosition === filterPosition) &&
      (filterName === '' || fullName.includes(filterName.toLowerCase()))
    );
    
    const matchesZeroBalanceFilter =
      filterZeroBalances === 'only'
        ? (emp.totalBalance === 0 || emp.totalBalance === null)
        : filterZeroBalances === 'exclude'
          ? (emp.totalBalance > 0 && emp.totalBalance !== null)
          : true;

    const matchesPendingBalanceFilter = filterPendingBalance
      ? (emp.pendingBalance || 0) > 0
      : true;

    return matchesFilters && matchesZeroBalanceFilter && matchesPendingBalanceFilter;
  });

  // Sorting function
  const sortedEmployees = React.useMemo(() => {
    let sortableItems = [...filteredEmployees];
    if (sortConfig.key !== null) {
      sortableItems.sort((a, b) => {
        let aValue, bValue;
        
        switch (sortConfig.key) {
          case 'employee':
            aValue = `${a.employee?.firstName || ''} ${a.employee?.lastName || ''}`.toLowerCase();
            bValue = `${b.employee?.firstName || ''} ${b.employee?.lastName || ''}`.toLowerCase();
            break;
          case 'department':
            aValue = a.employee?.department?.name || '';
            bValue = b.employee?.department?.name || '';
            break;
          case 'position':
            aValue = a.employee?.position || '';
            bValue = b.employee?.position || '';
            break;
          case 'leaveType':
            aValue = a.leaveType;
            bValue = b.leaveType;
            break;
          case 'totalBalance':
            aValue = a.totalBalance || 0;
            bValue = b.totalBalance || 0;
            break;
          case 'currentBalance':
            aValue = a.currentBalance || 0;
            bValue = b.currentBalance || 0;
            break;
          case 'previousBalance':
            aValue = a.previousBalance || 0;
            bValue = b.previousBalance || 0;
            break;
          case 'pendingBalance':
            aValue = a.pendingBalance || 0;
            bValue = b.pendingBalance || 0;
            break;
          case 'realVacation':
            aValue = calculateRealVacation(a.employee?.dateOfJoining);
            bValue = calculateRealVacation(b.employee?.dateOfJoining);
            break;
          default:
            return 0;
        }
        
        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableItems;
  }, [filteredEmployees, sortConfig]);

  // Group employee data by leave type for card view
  const getEmployeeLeaveData = () => {
  if (employees.length === 0) return [];

  const employee = employees[0]?.employee;
  const currentYear = new Date().getFullYear();
  const previousYear = currentYear - 1;

  // Group by leaveType
  const grouped = {};
  leaveTypes.forEach(type => {
    grouped[type] = {
      leaveType: type,
      current: null,
      previous: null
    };
  });

  // Populate from API data
  employees.forEach(item => {
    const { leaveType, year, balance, id } = item;
    if (!leaveTypes.includes(leaveType)) return;

    if (year === currentYear) {
      grouped[leaveType].current = { id, year, balance };
    } else if (year === previousYear) {
      grouped[leaveType].previous = { id, year, balance };
    }
  });

  // Enrich for UI
  return leaveTypes.map(leaveType => {
    const { current, previous } = grouped[leaveType];

    // Default annual vacation from employee
    const defaultAnnual = employee?.annualVacation || 0;
    const defaultBalances = {
      ANNUAL_LEAVE: defaultAnnual,
      SICK_LEAVE: 10,
      UNPAID_LEAVE: 3
    };

    const totalDefault = defaultBalances[leaveType] || 0;

    const currentBalance = current?.balance ?? 0;
    const previousBalance = previous?.balance ?? 0;

    const consumedCurrent = Math.max(0, totalDefault - currentBalance);

    return {
      type: leaveType,
      label: leaveTypeLabels[leaveType],
      icon: leaveTypeIcons[leaveType],
      color: leaveTypeColors[leaveType],
      total: totalDefault,
      remaining: currentBalance,
      consumed: consumedCurrent,
      percentage: totalDefault > 0 ? (currentBalance / totalDefault) * 100 : 0,

      // Pass IDs and previous balance for editing or display
      currentId: current?.id,
      previousId: previous?.id,
      previousBalance: previousBalance,
      previousYear: previousYear,
      currentYear: currentYear
    };
  });
};

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelected(sortedEmployees.map(emp => emp.employeeId));
    } else {
      setSelected([]);
    }
  };

  const handleSelect = (id) => {
    setSelected(prev => 
      prev.includes(id) 
        ? prev.filter(item => item !== id)
        : [...prev, id]
    );
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const handleEdit = (employee, year) => {
    // Create a unique identifier for this specific row and year
    const rowId = `${employee.employeeId}_${employee.leaveType}`;
    if (year === 'pending') {
      setEditingRow({ rowId, year });
      setEditValues({
        id: employee.currentId || employee.previousId || employee.id,
        pendingBalance: employee.pendingBalance || 0
      });
      return;
    }
    
    // Determine which ID to use based on the year
    const recordId = year === 'previous' ? employee.previousId : employee.currentId;
    const currentBalance = year === 'previous' ? employee.previousBalance : employee.currentBalance;
    
    setEditingRow({ rowId, year });
    
    // Set editValues with the correct record ID and balance
    setEditValues({
      id: recordId, // This is the actual database ID for the specific year
      employee: {
        employeeId: employee.employeeId
      },
      balance: currentBalance || 0,
      leaveType: employee.leaveType,
      year: year === 'previous' ? employee.previousYear : employee.currentYear
    });
  };

  const handleSave = async () => {
    try {
      if (!editValues.id) {
        showSnackbar('Cannot update: Missing record ID', 'error');
        return;
      }

      if (editingRow.year === 'pending') {
        const newPendingBalance = editValues.pendingBalance || 0;

        await updatePendingBalance(editValues.id, newPendingBalance);

        setEmployees(prev => prev.map(emp => {
          if ((emp.currentId || emp.previousId || emp.id) === editValues.id) {
            return {
              ...emp,
              pendingBalance: newPendingBalance
            };
          }
          return emp;
        }));

        showSnackbar('Pending balance updated successfully', 'success');
        setEditingRow(null);
        setEditValues({});
        return;
      }

      const employeeId = employees.find(e => 
        (editingRow.year === 'previous' ? e.previousId : e.currentId) === editValues.id
      )?.employeeId;
      
      await updateLeaveBalance(editValues.id, {
        id: editValues.id,
        balance: editValues.balance,
        leaveType: editValues.leaveType,
        year: editValues.year,
        employee: { employeeId }
      });
      
      // Update the local state
      setEmployees(prev => prev.map(emp => {
        // Check if this employee matches the one being edited
        if ((editingRow.year === 'previous' && emp.previousId === editValues.id) ||
            (editingRow.year === 'current' && emp.currentId === editValues.id)) {
          
          // Return updated employee with the changed balance
          if (editingRow.year === 'previous') {
            const newPreviousBalance = editValues.balance || 0;
            const newTotalBalance = newPreviousBalance + (emp.currentBalance || 0);
            return {
              ...emp,
              previousBalance: newPreviousBalance,
              totalBalance: newTotalBalance
            };
          } else {
            const newCurrentBalance = editValues.balance || 0;
            const newTotalBalance = (emp.previousBalance || 0) + newCurrentBalance;
            return {
              ...emp,
              currentBalance: newCurrentBalance,
              totalBalance: newTotalBalance
            };
          }
        }
        return emp;
      }));
      
      showSnackbar('Leave balance updated successfully', 'success');
    } catch (error) {
      console.error('Error updating leave balance:', error);
      showSnackbar('Failed to update leave balance', 'error');
    }

    setEditingRow(null);
    setEditValues({});
  };

  const handleCancel = () => {
    setEditingRow(null);
    setEditValues({});
  };

  // Helper function to check if a specific cell is being edited
  const isCellEditing = (employee, year) => {
    if (!editingRow) return false;
    const rowId = `${employee.employeeId}_${employee.leaveType}`;
    return editingRow.rowId === rowId && editingRow.year === year;
  };

  const handleBulkUpdate = () => {
    if (selected.length === 0) {
      showSnackbar('Please select employees to update', 'warning');
      return;
    }
    setBulkUpdateDialog(true);
  };

  const confirmBulkUpdate = async () => {
    try {
      // For bulk update, we need to update both current year records for selected employees
      const updates = [];
      
      sortedEmployees.forEach(emp => {
        if (selected.includes(emp.employeeId) && emp.currentId) {
          updates.push(
            updateLeaveBalance(emp.currentId, {
              balance: bulkValues.balance ? parseFloat(bulkValues.balance) : emp.currentBalance,
              leaveType: bulkValues.leaveType
            })
          );
        }
      });
      
      await Promise.all(updates);
      
      // Update local state
      setEmployees(prev => 
        prev.map(emp => 
          selected.includes(emp.employeeId) && emp.currentId
            ? {
                ...emp,
                currentBalance: bulkValues.balance ? parseFloat(bulkValues.balance) : emp.currentBalance,
                leaveType: bulkValues.leaveType,
                totalBalance: (emp.previousBalance || 0) + (bulkValues.balance ? parseFloat(bulkValues.balance) : (emp.currentBalance || 0))
              }
            : emp
        )
      );
      
      showSnackbar(`Updated ${updates.length} employees successfully`, 'success');
    } catch (error) {
      console.error('Bulk update failed:', error);
      showSnackbar('Bulk update failed', 'error');
    }

    setBulkUpdateDialog(false);
    setBulkValues({ balance: '', leaveType: 'ANNUAL_LEAVE' });
    setSelected([]);
  };

  // Export to Excel
  const exportToExcel = () => {
    const exportData = sortedEmployees.map(emp => ({
      Employee: `${emp.employee?.firstName || ''} ${emp.employee?.lastName || ''}`,
      Department: emp.employee?.department?.name || '',
      // Position: emp.employee?.position || '',
      'Leave Type': leaveTypeLabels[emp.leaveType] || emp.leaveType,
      'Previous Year Balance': `${emp.previousBalance || 0} days`,
      'Current Year Balance': `${emp.currentBalance || 0} days`,
      'Total Balance': `${emp.totalBalance || 0} days`
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Leave Balance');
    
    // Auto-size columns
    const colWidths = Object.keys(exportData[0] || {}).map(key => ({
      wch: Math.max(key.length, ...exportData.map(row => String(row[key] || '').length))
    }));
    ws['!cols'] = colWidths;
    
    XLSX.writeFile(wb, `leave-balance-${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  // Export to PDF with improved styling - PORTRAIT MODE
  const exportToPDF = () => {
    const data = sortedEmployees.map(emp => ({
      Employee: `${emp.employee?.firstName || 'N/A'} ${emp.employee?.lastName || 'N/A'}`,
      Department: emp.employee?.department?.name || 'N/A',
      // Position: emp.employee?.position || 'N/A',
      'Leave Type': leaveTypeLabels[emp.leaveType] || emp.leaveType,
      'Previous Year': `${emp.previousBalance || 0} days`,
      'Current Year': `${emp.currentBalance || 0} days`,
      'Total': `${emp.totalBalance || 0} days`
    }));

    const doc = new jsPDF('p', 'mm', 'a4'); // Portrait mode
    const pageWidth = doc.internal.pageSize.width;
    const pageHeight = doc.internal.pageSize.height;
    const exportDate = new Date().toLocaleDateString('en-GB');
    const margin = 8; // Reduced margin for tighter layout

    // --- Title Section - centered ---
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(40, 58, 139); // Dark blue
    doc.text('Leave Balance Report', pageWidth / 2, 15, { align: 'center' });

    // --- Stats Bar ---
    doc.setFillColor(232, 245, 255); // Light blue background
    doc.rect(margin, 18, pageWidth - (margin * 2), 8, 'F'); // Reduced height

    doc.setFontSize(8);
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'normal');

    // Left-aligned: Date and stats text together
    doc.text(`Export Date: ${exportDate} • Total Records: ${sortedEmployees.length}`, pageWidth / 2, 23, { align: 'center' });

    // --- Table ---
    const tableColumns = ['Employee', 'Department', 'Position', 'Leave Type', 'Previous Year', 'Current Year', 'Total'];
    const tableRows = data.map(item => [
      item.Employee,
      item.Department,
      // item.Position,
      item['Leave Type'],
      item['Previous Year'],
      item['Current Year'],
      item['Total']
    ]);

    // Calculate column widths to fill available space
    const availableWidth = pageWidth - (2 * margin);
    
    // More precise width calculation with min/max constraints
    const headerWidths = tableColumns.map(header => header.length * 2.2);
    const dataWidths = tableColumns.map((_, colIndex) => 
      Math.max(...tableRows.map(row => row[colIndex]?.toString().length || 0)) * 2
    );
    
    const combinedWidths = tableColumns.map((_, index) => 
      Math.max(headerWidths[index], dataWidths[index], 12) // Min width 12
    );
    
    const totalEstimatedWidth = combinedWidths.reduce((sum, w) => sum + w, 0);
    const adjustedWidths = combinedWidths.map(width => 
      (width / totalEstimatedWidth) * availableWidth
    );
    
    // Create column styles
    const columnStyles = {};
    adjustedWidths.forEach((width, index) => {
      columnStyles[index] = { cellWidth: width };
    });

    // Table with reduced spacing
    autoTable(doc, {
      head: [tableColumns],
      body: tableRows,
      startY: 30, // Start right after stats bar
      margin: { left: margin, right: margin },
      styles: { 
        fontSize: 6,
        cellPadding: 0.8, // Reduced padding
        minCellHeight: 4, // Reduced height
        font: 'helvetica'
      },
      headStyles: { 
        fillColor: [40, 58, 139], // Dark blue for header row
        textColor: 255,
        fontSize: 7,
        fontStyle: 'bold',
        minCellHeight: 5, // Reduced height
        cellPadding: { top: 0.5, bottom: 0.5, left: 0.7, right: 0.7 }
      },
      bodyStyles: {
        fillColor: [255, 255, 255], // White background for all body rows
      },
      alternateRowStyles: { 
        fillColor: [248, 249, 250] // Light gray for alternating rows
      },
      theme: 'grid',
      columnStyles: columnStyles,
      bodyStyles: {
        lineWidth: 0.1 // Thinner borders
      },
      headStyles: {
        lineWidth: 0.1 // Thinner borders
      },
      didParseCell: function(data) {
        // Apply blue background to header row
        if (data.section === 'head') {
          data.cell.styles.fillColor = [40, 58, 139]; // Dark blue
          data.cell.styles.textColor = 255; // White text
        }
        
        // Apply light blue background to first row of data
        if (data.section === 'body' && data.row.index === 0) {
          data.cell.styles.fillColor = [232, 245, 255]; // Light blue background
          data.cell.styles.textColor = 0; // Black text
        }
        
        // Text wrapping for employee names
        if (data.section === 'body' && data.column.index === 0) {
          const text = data.cell.text[0];
          if (text && text.length > 25) {
            data.cell.text = doc.splitTextToSize(text, adjustedWidths[0] - 2);
          }
        }
      },
      didDrawPage: function (data) {
        // FOOTER AT THE VERY BOTTOM OF THE PAGE
        const footerY = pageHeight - 8; // Positioned exactly 8mm from bottom
        
        // Thin line above footer
        doc.setDrawColor(220, 220, 220);
        doc.setLineWidth(0.2);
        doc.line(margin, footerY - 5, pageWidth - margin, footerY - 5);
        
        // Footer content - compact
        doc.setFontSize(7);
        doc.setTextColor(80);
        
        // Left: Company name
        doc.text('AxoPeople', margin, footerY, { align: 'left' });
        
        // Center: Page number
        doc.text(`Page ${doc.internal.getCurrentPageInfo().pageNumber}`, pageWidth / 2, footerY, { align: 'center' });
        
        // Right: Export date
        doc.text(exportDate, pageWidth - margin, footerY, { align: 'right' });
      }
    });

    doc.save(`leave-balance-${new Date().toISOString().split('T')[0]}.pdf`);
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const getBalanceColor = (balance) => {
    if (balance >= 6) return 'success';
    if (balance >= 1) return 'warning';
    return 'error';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Employee Card View
  if (IsEmployee) {
    const leaveData = getEmployeeLeaveData();
    const employee = employees[0]?.employee;
    
    return <LeaveBalance
        employee={employee}
        leaveData={leaveData}
        snackbar={snackbar}
        setSnackbar={setSnackbar}
      />;
  }

  // Admin Table View
  return (
    <Box sx={{ p: 3 }} style={{ width: '100%', padding: '0' }}>
      <PageHeader 
        title="Leave Balance" 
        subtitle="Manage and track employee leave balances"
      />
      <Grid container spacing={3}>
        {/* Filter Controls */}
        <Grid item xs={12} style={{ width: '100%' }}>
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Search by Name"
                    variant="outlined"
                    value={filterName}
                    onChange={(e) => setFilterName(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <FormControl fullWidth size="small" style={{ minWidth: 150 }}>
                    <InputLabel>Filter by Department</InputLabel>
                    <Select
                      value={filterDepartment}
                      label="Filter by Department"
                      onChange={(e) => setFilterDepartment(e.target.value)}
                    >
                      <MenuItem value="">All Departments</MenuItem>
                      {departments.map(dept => (
                        <MenuItem key={dept} value={dept}>{dept}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                {/* <Grid item xs={12} sm={3}>
                  <FormControl fullWidth size="small" style={{ minWidth: 150 }}>
                    <InputLabel>Filter by Position</InputLabel>
                    <Select
                      value={filterPosition}
                      label="Filter by Position"
                      onChange={(e) => setFilterPosition(e.target.value)}
                    >
                      <MenuItem value="">All Positions</MenuItem>
                      {positions.map(pos => (
                        <MenuItem key={pos} value={pos}>{pos}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid> */}
                <Grid item xs={12} sm={3}>
                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Button
                      variant="outlined"
                      onClick={() => {
                        setFilterDepartment('');
                        // setFilterPosition('');
                        setFilterName('');
                        setFilterZeroBalances('all');
                        setFilterPendingBalance(false);
                      }}
                    >
                      Clear Filters
                    </Button>
                    <Button
                      variant={filterZeroBalances === 'only' ? 'contained' : 'outlined'}
                      onClick={() => setFilterZeroBalances(filterZeroBalances === 'only' ? 'all' : 'only')}
                      color="error"
                    >
                      Only 0 Balances
                    </Button>
                    <Button
                      variant={filterZeroBalances === 'exclude' ? 'contained' : 'outlined'}
                      onClick={() => setFilterZeroBalances(filterZeroBalances === 'exclude' ? 'all' : 'exclude')}
                      color="warning"
                    >
                      Exclude 0 Balances
                    </Button>
                    <Button
                      variant={filterPendingBalance ? 'contained' : 'outlined'}
                      onClick={() => setFilterPendingBalance(prev => !prev)}
                      color="info"
                    >
                      Pending &gt; 0
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<Update />}
                      onClick={handleBulkUpdate}
                      disabled={selected.length === 0}
                    >
                      Bulk Update ({selected.length})
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<FileDownload />}
                      onClick={exportToExcel}
                      color="secondary"
                    >
                      Export Excel
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<PictureAsPdf />}
                      onClick={exportToPDF}
                      color="error"
                    >
                      Export PDF
                    </Button>
                  </Stack>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Main Table */}
        <Grid item xs={12} style={{ width: '100%' }}>
          <Card>
            <CardContent sx={{ p: 0 }}>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          indeterminate={selected.length > 0 && selected.length < sortedEmployees.length}
                          checked={sortedEmployees.length > 0 && selected.length === sortedEmployees.length}
                          onChange={handleSelectAll}
                        />
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" onClick={() => handleSort('employee')}>
                          <strong>Employee</strong>
                          <IconButton size="small">
                            <Sort />
                          </IconButton>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" onClick={() => handleSort('department')}>
                          <strong>Department</strong>
                          <IconButton size="small">
                            <Sort />
                          </IconButton>
                        </Box>
                      </TableCell>
                      {/* <TableCell>
                        <Box display="flex" alignItems="center" onClick={() => handleSort('position')}>
                          <strong>Position</strong>
                          <IconButton size="small">
                            <Sort />
                          </IconButton>
                        </Box>
                      </TableCell> */}
                      <TableCell>
                        <Box display="flex" alignItems="center" onClick={() => handleSort('leaveType')}>
                          <strong>Leave Type</strong>
                          <IconButton size="small">
                            <Sort />
                          </IconButton>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" onClick={() => handleSort('previousBalance')}>
                          <strong>{previousYear} Balance</strong>
                          <IconButton size="small">
                            <Sort />
                          </IconButton>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" onClick={() => handleSort('currentBalance')}>
                          <strong>{currentYear} Balance</strong>
                          <IconButton size="small">
                            <Sort />
                          </IconButton>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" onClick={() => handleSort('totalBalance')}>
                          <strong>Total</strong>
                          <IconButton size="small">
                            <Sort />
                          </IconButton>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" onClick={() => handleSort('pendingBalance')}>
                          <strong>Pending Balance</strong>
                          <IconButton size="small">
                            <Sort />
                          </IconButton>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" onClick={() => handleSort('realVacation')}>
                          <strong>Real Vacation</strong>
                          <IconButton size="small">
                            <Sort />
                          </IconButton>
                        </Box>
                      </TableCell>
                      <TableCell><strong>Actions</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedEmployees.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={10} align="center">
                          No leave balance records found
                        </TableCell>
                      </TableRow>
                    ) : (
                      sortedEmployees.map((employee) => {
                        const rowId = `${employee.employeeId}_${employee.leaveType}`;
                        
                        return (
                          <TableRow key={rowId} hover>
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={selected.includes(employee.employeeId)}
                                onChange={() => handleSelect(employee.employeeId)}
                              />
                            </TableCell>
                            <TableCell>
                              {employee.employee 
                                ? `${employee.employee.firstName || ''} ${employee.employee.lastName || ''}`
                                : 'Unknown Employee'}
                            </TableCell>
                            <TableCell>
                              <Chip 
                                label={employee.employee?.department?.name || 'N/A'} 
                                size="small" 
                                variant="outlined"
                              />
                            </TableCell>
                            {/* <TableCell>{employee.employee?.position || 'N/A'}</TableCell> */}
                            <TableCell>
                              <Chip 
                                label={leaveTypeLabels[employee.leaveType] || employee.leaveType}
                                size="small"
                                color={employee.leaveType === 'ANNUAL_LEAVE' ? 'primary' : employee.leaveType === 'SICK_LEAVE' ? 'warning' : 'default'}
                              />
                            </TableCell>
                            
                            {/* Previous Year Balance - Editable */}
                            <TableCell>
                              {isCellEditing(employee, 'previous') ? (
                                <TextField
                                  size="small"
                                  type="number"
                                  inputProps={{ step: "0.5", min: "0" }}
                                  value={editValues.balance || ''}
                                  onChange={(e) => {
                                    const val = e.target.value === '' ? '' : parseFloat(e.target.value);
                                    setEditValues(prev => ({ 
                                      ...prev, 
                                      id: employee.previousId,
                                      year: employee.previousYear,
                                      balance: isNaN(val) ? '' : val 
                                    }));
                                  }}
                                  sx={{ width: 80 }}
                                  autoFocus
                                />
                              ) : (
                                employee.previousBalance !== undefined && employee.previousBalance !== null ? (
                                  <Chip 
                                    label={`${employee.previousBalance} days`} 
                                    size="small" 
                                    color={getBalanceColor(employee.previousBalance)} 
                                  />
                                ) : (
                                  '—'
                                )
                              )}
                            </TableCell>
                            
                            {/* Current Year Balance - Editable */}
                            <TableCell>
                              {isCellEditing(employee, 'current') ? (
                                <TextField
                                  size="small"
                                  type="number"
                                  inputProps={{ step: "0.5", min: "0" }}
                                  value={editValues.balance || ''}
                                  onChange={(e) => {
                                    const val = e.target.value === '' ? '' : parseFloat(e.target.value);
                                    setEditValues(prev => ({ 
                                      ...prev, 
                                      id: employee.currentId,
                                      year: employee.currentYear,
                                      balance: isNaN(val) ? '' : val 
                                    }));
                                  }}
                                  sx={{ width: 80 }}
                                  autoFocus
                                />
                              ) : (
                                employee.currentBalance !== undefined && employee.currentBalance !== null ? (
                                  <Chip 
                                    label={`${employee.currentBalance} days`} 
                                    size="small" 
                                    color={getBalanceColor(employee.currentBalance)} 
                                  />
                                ) : (
                                  '—'
                                )
                              )}
                            </TableCell>
                            
                            {/* Total Balance - Read Only */}
                            <TableCell>
                              <Chip 
                                label={`${employee.totalBalance || 0} days`}
                                size="small"
                                color={getBalanceColor(employee.totalBalance || 0)}
                              />
                            </TableCell>

                            {/* Pending Balance - Editable */}
                            <TableCell>
                              {isCellEditing(employee, 'pending') ? (
                                <TextField
                                  size="small"
                                  type="number"
                                  inputProps={{ step: "0.5", min: "0" }}
                                  value={editValues.pendingBalance ?? ''}
                                  onChange={(e) => {
                                    const val = e.target.value === '' ? '' : parseFloat(e.target.value);
                                    setEditValues(prev => ({
                                      ...prev,
                                      id: employee.currentId || employee.previousId || employee.id,
                                      pendingBalance: isNaN(val) ? '' : val
                                    }));
                                  }}
                                  sx={{ width: 80 }}
                                  autoFocus
                                />
                              ) : (
                                <Chip
                                  label={`${employee.pendingBalance || 0} days`}
                                  size="small"
                                  color={getBalanceColor(employee.pendingBalance || 0)}
                                />
                              )}
                            </TableCell>

                            {/* Real Vacation - Read Only */}
                            <TableCell>
                              <Chip
                                label={`${calculateRealVacation(employee.employee?.dateOfJoining)} days`}
                                size="small"
                                sx={{
                                  backgroundColor: '#e3f2fd',
                                  color: '#0d47a1',
                                  fontWeight: 'bold'
                                }}
                              />
                            </TableCell>
                            
                            {/* Actions - Edit buttons for each year */}
                            <TableCell>
                              {editingRow && editingRow.rowId === rowId ? (
                                <Stack direction="row" spacing={1}>
                                  <IconButton size="small" color="primary" onClick={handleSave}>
                                    <Save />
                                  </IconButton>
                                  <IconButton size="small" color="secondary" onClick={handleCancel}>
                                    <Cancel />
                                  </IconButton>
                                </Stack>
                              ) : (
                                <Stack direction="row" spacing={1}>
                                  {/* Edit Previous Year button - only if previous record exists */}
                                  {employee.previousId && employee.previousBalance !== null && (
                                    <IconButton 
                                      size="small" 
                                      onClick={() => handleEdit(employee, 'previous')}
                                      title="Edit previous year balance"
                                    >
                                      <Edit fontSize="small" />
                                    </IconButton>
                                  )}
                                  
                                  {/* Edit Current Year button - only if current record exists */}
                                  {employee.currentId && employee.currentBalance !== null && (
                                    <IconButton 
                                      size="small" 
                                      onClick={() => handleEdit(employee, 'current')}
                                      title="Edit current year balance"
                                    >
                                      <Edit fontSize="small" />
                                    </IconButton>
                                  )}

                                  {/* Edit Pending Balance button */}
                                  {(employee.currentId || employee.previousId || employee.id) && (
                                    <IconButton
                                      size="small"
                                      onClick={() => handleEdit(employee, 'pending')}
                                      title="Edit pending balance"
                                    >
                                      <Edit fontSize="small" />
                                    </IconButton>
                                  )}
                                </Stack>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Bulk Update Dialog */}
      <Dialog open={bulkUpdateDialog} onClose={() => setBulkUpdateDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Bulk Update Leave Balance</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Update current year leave balance for {selected.length} selected employee(s)
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="New Balance (days)"
                  type="number"
                  value={bulkValues.balance}
                  onChange={(e) => setBulkValues(prev => ({ ...prev, balance: e.target.value }))}
                  helperText="Leave empty to keep current balance"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Leave Type</InputLabel>
                  <Select
                    value={bulkValues.leaveType}
                    label="Leave Type"
                    onChange={(e) => setBulkValues(prev => ({ ...prev, leaveType: e.target.value }))}
                  >
                    {leaveTypes.map(type => (
                      <MenuItem key={type} value={type}>{leaveTypeLabels[type]}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkUpdateDialog(false)}>Cancel</Button>
          <Button onClick={confirmBulkUpdate} variant="contained">Update</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default LeaveBalanceManager;
// import React, { useState, useEffect } from 'react';
// import {
//   Box, Card, CardContent, Typography, Table, TableBody,
//   TableCell, TableContainer, TableHead, TableRow, Checkbox,
//   TextField, Button, FormControl, InputLabel, Select,
//   MenuItem, Chip, IconButton, Grid,
//   Alert, Snackbar, Dialog, DialogTitle, DialogContent, DialogActions, Stack, CircularProgress,
//   LinearProgress, Paper
// } from '@mui/material';
// import {
//   Edit,
//   Save,
//   Cancel,
//   Update,
//   CalendarToday,
//   LocalHospital,
//   Work,
//   TrendingDown,
//   Sort,
//   PictureAsPdf,
//   FileDownload
// } from '@mui/icons-material';
// import { getAllLeaveBalanceCombined, getAllLeaveBalance, updateLeaveBalance, getMyLeaveBalance } from '../../services/leaveBalanceAPIs';
// import LeaveBalance from '../../components/dash/BalanceOverview';
// // import {transformLeaveBalances} from './helper.js';
// import PageHeader from '../../components/headers/MiniHeader';

// // Import libraries for export functionality
// import * as XLSX from 'xlsx';
// import jsPDF from 'jspdf';
// import autoTable from 'jspdf-autotable';

// const LeaveBalanceManager = ({ IsEmployee=false }) => {
//   const [employees, setEmployees] = useState([]);
//   const [selected, setSelected] = useState([]);
//   const [editingId, setEditingId] = useState(null);
//   const [editValues, setEditValues] = useState({});
//   const [filterDepartment, setFilterDepartment] = useState('');
//   const [filterPosition, setFilterPosition] = useState('');
//   const [filterName, setFilterName] = useState('');
//   const [filterZeroBalances, setFilterZeroBalances] = useState('all'); // 'all', 'only', 'exclude'
//   const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

//   const [bulkUpdateDialog, setBulkUpdateDialog] = useState(false);
//   const [bulkValues, setBulkValues] = useState({ balance: '', leaveType: 'ANNUAL_LEAVE' });
//   const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
//   const [loading, setLoading] = useState(true);

//   const leaveTypeLabels = {
//     SICK_LEAVE: 'Sick Leave',
//     ANNUAL_LEAVE: 'Annual Leave',
//     UNPAID_LEAVE: 'Unpaid Leave',
//   };

//   const leaveTypeIcons = {
//     ANNUAL_LEAVE: CalendarToday,
//     SICK_LEAVE: LocalHospital,
//     UNPAID_LEAVE: Work,
//   };

//   const leaveTypeColors = {
//     ANNUAL_LEAVE: '#4CAF50',
//     SICK_LEAVE: '#FF9800',
//     UNPAID_LEAVE: '#9E9E9E',
//   };

//   // Default balances based on your backend logic
//   const getDefaultBalance = (employee, position, department) => {
//     if (employee.annualVacation) {
//       return { ANNUAL_LEAVE: employee.annualVacation, SICK_LEAVE: 10, UNPAID_LEAVE: 5 };
//     } else {
//       return { ANNUAL_LEAVE: 0, SICK_LEAVE: 10, UNPAID_LEAVE: 3 };
//     }
//   };

//   const currentYear = new Date().getFullYear();
//   const previousYear = currentYear - 1;

//   useEffect(() => {
//     const fetchLeaveBalances = async () => {
//       try {
//         let data;
//         if (IsEmployee) {
//           data = await getMyLeaveBalance();
//           console.log('Employee Leave Balance:', data);
//           const transformedData = data.map(item => ({
//             ...item,
//             employee: data[0]?.employee || {},
//             department: data[0]?.employee?.department?.name || '',
//             position: data[0]?.employee?.position || '',

//           }));
//           setEmployees(transformedData);
//         } else {
//           data = await getAllLeaveBalanceCombined();
//           console.log('Transformed Leave Balances:', data);
//           setEmployees(data);
//           //setEmployees(data);
//         }
//       } catch (error) {
//         console.error('Error fetching leave balances:', error);
//         showSnackbar('Error loading leave balances', 'error');
//       } finally {
//         setLoading(false);
//       }
//     };
//     fetchLeaveBalances();
//   }, [IsEmployee]);

//   // Get departments and positions from valid employee data
//   const departments = [...new Set(employees
//     .filter(emp => emp.department || emp.employee?.department?.name)
//     .map(emp => emp.department || emp.employee?.department?.name))];
  
//   const positions = [...new Set(employees
//     .filter(emp => emp.position || emp.employee?.position)
//     .map(emp => emp.position || emp.employee?.position))];
  
//   //const leaveTypes = ['ANNUAL_LEAVE', 'SICK_LEAVE', 'UNPAID_LEAVE'];
//   const leaveTypes = ['ANNUAL_LEAVE'];

//   const filteredEmployees = employees.filter(emp => {
//     const firstName = emp.employee?.firstName || '';
//     const lastName = emp.employee?.lastName || '';
//     const fullName = `${firstName} ${lastName}`.toLowerCase();
    
//     const empDepartment = emp.employee?.department?.name || '';
//     const empPosition = emp.employee?.position || '';
    
//     const matchesFilters = (
//       (filterDepartment === '' || empDepartment === filterDepartment) &&
//       (filterPosition === '' || empPosition === filterPosition) &&
//       (filterName === '' || fullName.includes(filterName.toLowerCase()))
//     );
    
//     if (filterZeroBalances === 'only') {
//       return matchesFilters && emp.balance === 0;
//     } else if (filterZeroBalances === 'exclude') {
//       return matchesFilters && emp.balance > 0;
//     }
    
//     return matchesFilters;
//   });

//   // Sorting function
//   const sortedEmployees = React.useMemo(() => {
//     let sortableItems = [...filteredEmployees];
//     if (sortConfig.key !== null) {
//       sortableItems.sort((a, b) => {
//         let aValue, bValue;
        
//         switch (sortConfig.key) {
//           case 'employee':
//             aValue = `${a.employee?.firstName || ''} ${a.employee?.lastName || ''}`.toLowerCase();
//             bValue = `${b.employee?.firstName || ''} ${b.employee?.lastName || ''}`.toLowerCase();
//             break;
//           case 'department':
//             aValue = a.employee?.department?.name || '';
//             bValue = b.employee?.department?.name || '';
//             break;
//           case 'position':
//             aValue = a.employee?.position || '';
//             bValue = b.employee?.position || '';
//             break;
//           case 'leaveType':
//             aValue = a.leaveType;
//             bValue = b.leaveType;
//             break;
//           case 'balance':
//             aValue = a.balance;
//             bValue = b.balance;
//             break;
//           case 'year':
//             aValue = a.year;
//             bValue = b.year;
//             break;
//           default:
//             return 0;
//         }
        
//         if (aValue < bValue) {
//           return sortConfig.direction === 'asc' ? -1 : 1;
//         }
//         if (aValue > bValue) {
//           return sortConfig.direction === 'asc' ? 1 : -1;
//         }
//         return 0;
//       });
//     }
//     return sortableItems;
//   }, [filteredEmployees, sortConfig]);

//   // Group employee data by leave type for card view
//   const getEmployeeLeaveData = () => {
//     if (employees.length === 0) return [];
    
//     const employee = employees[0]?.employee;
//     const position = employee?.position || '';
//     const department = employee?.department?.name || '';
//     const defaultBalances = getDefaultBalance(employee, position, department);
    
//     return leaveTypes.map(leaveType => {
//       const leaveRecord = employees.find(emp => emp.leaveType === leaveType);
//       const currentBalance = leaveRecord?.balance || 0;
//       const defaultBalance = defaultBalances[leaveType] || 0;
//       const consumed = Math.max(0, defaultBalance - currentBalance);
      
//       return {
//         type: leaveType,
//         label: leaveTypeLabels[leaveType],
//         icon: leaveTypeIcons[leaveType],
//         color: leaveTypeColors[leaveType],
//         total: defaultBalance,
//         remaining: currentBalance,
//         consumed: consumed,
//         percentage: defaultBalance > 0 ? (currentBalance / defaultBalance) * 100 : 0
//       };
//     });
//   };

//   const handleSelectAll = (event) => {
//     if (event.target.checked) {
//       setSelected(sortedEmployees.map(emp => emp.id));
//     } else {
//       setSelected([]);
//     }
//   };

//   const handleSelect = (id) => {
//     setSelected(prev => 
//       prev.includes(id) 
//         ? prev.filter(item => item !== id)
//         : [...prev, id]
//     );
//   };

//   const handleSort = (key) => {
//     let direction = 'asc';
//     if (sortConfig.key === key && sortConfig.direction === 'asc') {
//       direction = 'desc';
//     }
//     setSortConfig({ key, direction });
//   };

//   const handleEdit = (employee) => {
//     setEditingId(employee.id);
//     setEditValues(employee);
//   };

//   const handleSave = async () => {
//     try {
//       await updateLeaveBalance(editingId, editValues);
//       setEmployees(prev => prev.map(emp => 
//         emp.id === editingId ? { ...emp, ...editValues } : emp
//       ));
//       showSnackbar('Employee leave balance updated successfully', 'success');
//     } catch (error) {
//       console.error('Error updating leave balance:', error);
//       showSnackbar('Failed to update leave balance', 'error');
//     }

//     setEditingId(null);
//     setEditValues({});
//   };

//   const handleCancel = () => {
//     setEditingId(null);
//     setEditValues({});
//   };

//   const handleBulkUpdate = () => {
//     if (selected.length === 0) {
//       showSnackbar('Please select employees to update', 'warning');
//       return;
//     }
//     setBulkUpdateDialog(true);
//   };

//   const confirmBulkUpdate = async () => {
//     try {
//       await Promise.all(
//         selected.map(id => {
//           const employee = employees.find(emp => emp.id === id);
//           const updatedEmployee = {
//             ...employee,
//             balance: bulkValues.balance ? parseInt(bulkValues.balance) : employee.balance,
//             leaveType: bulkValues.leaveType
//           };
//           return updateLeaveBalance(id, updatedEmployee);
//         })
//       );
      
//       setEmployees(prev => 
//         prev.map(emp => 
//           selected.includes(emp.id)
//             ? {
//                 ...emp,
//                 balance: bulkValues.balance ? parseInt(bulkValues.balance) : emp.balance,
//                 leaveType: bulkValues.leaveType
//               }
//             : emp
//         )
//       );
      
//       showSnackbar(`Updated ${selected.length} employees successfully`, 'success');
//     } catch (error) {
//       console.error('Bulk update failed:', error);
//       showSnackbar('Bulk update failed', 'error');
//     }

//     setBulkUpdateDialog(false);
//     setBulkValues({ balance: '', leaveType: 'ANNUAL_LEAVE' });
//     setSelected([]);
//   };

//   // Export to Excel
//   const exportToExcel = () => {
//     const exportData = sortedEmployees.map(emp => ({
//       Employee: `${emp.employee?.firstName || ''} ${emp.employee?.lastName || ''}`,
//       Department: emp.employee?.department?.name || '',
//       Position: emp.employee?.position || '',
//       'Leave Type': leaveTypeLabels[emp.leaveType] || emp.leaveType,
//       Balance: `${emp.balance} days`,
//       Year: emp.year
//     }));

//     const ws = XLSX.utils.json_to_sheet(exportData);
//     const wb = XLSX.utils.book_new();
//     XLSX.utils.book_append_sheet(wb, ws, 'Leave Balance');
    
//     // Auto-size columns
//     const colWidths = Object.keys(exportData[0] || {}).map(key => ({
//       wch: Math.max(key.length, ...exportData.map(row => String(row[key] || '').length))
//     }));
//     ws['!cols'] = colWidths;
    
//     XLSX.writeFile(wb, `leave-balance-${new Date().toISOString().split('T')[0]}.xlsx`);
//   };

// // Export to PDF with improved styling - PORTRAIT MODE
// const exportToPDF = () => {
//   const data = sortedEmployees.map(emp => ({
//     Employee: `${emp.employee?.firstName || 'N/A'} ${emp.employee?.lastName || 'N/A'}`,
//     Department: emp.employee?.department?.name || 'N/A',
//     Position: emp.employee?.position || 'N/A',
//     'Leave Type': leaveTypeLabels[emp.leaveType] || emp.leaveType,
//     Balance: `${emp.balance} days`,
//     Year: emp.year
//   }));

//   const doc = new jsPDF('p', 'mm', 'a4'); // Portrait mode
//   const pageWidth = doc.internal.pageSize.width;
//   const pageHeight = doc.internal.pageSize.height;
//   const exportDate = new Date().toLocaleDateString('en-GB');
//   const margin = 8; // Reduced margin for tighter layout

//   // --- Title Section - centered ---
//   doc.setFontSize(18);
//   doc.setFont('helvetica', 'bold');
//   doc.setTextColor(40, 58, 139); // Dark blue
//   doc.text('Leave Balance Report', pageWidth / 2, 15, { align: 'center' });

//   // --- Stats Bar ---
//   doc.setFillColor(232, 245, 255); // Light blue background
//   doc.rect(margin, 18, pageWidth - (margin * 2), 8, 'F'); // Reduced height

//   doc.setFontSize(8);
//   doc.setTextColor(0, 0, 0);
//   doc.setFont('helvetica', 'normal');

//   // Left-aligned: Date and stats text together
//   doc.text(`Export Date: ${exportDate} • Total Records: ${sortedEmployees.length}`, pageWidth / 2, 23, { align: 'center' });

//   // --- Table ---
//   const tableColumns = ['Employee', 'Department', 'Position', 'Leave Type', 'Balance', 'Year'];
//   const tableRows = data.map(item => [
//     item.Employee,
//     item.Department,
//     item.Position,
//     item['Leave Type'],
//     item.Balance,
//     item.Year
//   ]);

//   // Calculate column widths to fill available space
//   const availableWidth = pageWidth - (2 * margin);
  
//   // More precise width calculation with min/max constraints
//   const headerWidths = tableColumns.map(header => header.length * 2.2);
//   const dataWidths = tableColumns.map((_, colIndex) => 
//     Math.max(...tableRows.map(row => row[colIndex]?.toString().length || 0)) * 2
//   );
  
//   const combinedWidths = tableColumns.map((_, index) => 
//     Math.max(headerWidths[index], dataWidths[index], 12) // Min width 12
//   );
  
//   const totalEstimatedWidth = combinedWidths.reduce((sum, w) => sum + w, 0);
//   const adjustedWidths = combinedWidths.map(width => 
//     (width / totalEstimatedWidth) * availableWidth
//   );
  
//   // Create column styles
//   const columnStyles = {};
//   adjustedWidths.forEach((width, index) => {
//     columnStyles[index] = { cellWidth: width };
//   });

//   // Table with reduced spacing
//   autoTable(doc, {
//     head: [tableColumns],
//     body: tableRows,
//     startY: 30, // Start right after stats bar
//     margin: { left: margin, right: margin },
//     styles: { 
//       fontSize: 6,
//       cellPadding: 0.8, // Reduced padding
//       minCellHeight: 4, // Reduced height
//       font: 'helvetica'
//     },
//     headStyles: { 
//       fillColor: [40, 58, 139], // Dark blue for header row
//       textColor: 255,
//       fontSize: 7,
//       fontStyle: 'bold',
//       minCellHeight: 5, // Reduced height
//       cellPadding: { top: 0.5, bottom: 0.5, left: 0.7, right: 0.7 }
//     },
//     bodyStyles: {
//       fillColor: [255, 255, 255], // White background for all body rows
//     },
//     alternateRowStyles: { 
//       fillColor: [248, 249, 250] // Light gray for alternating rows
//     },
//     theme: 'grid',
//     columnStyles: columnStyles,
//     bodyStyles: {
//       lineWidth: 0.1 // Thinner borders
//     },
//     headStyles: {
//       lineWidth: 0.1 // Thinner borders
//     },
//     didParseCell: function(data) {
//       // Apply blue background to header row
//       if (data.section === 'head') {
//         data.cell.styles.fillColor = [40, 58, 139]; // Dark blue
//         data.cell.styles.textColor = 255; // White text
//       }
      
//       // Apply light blue background to first row of data
//       if (data.section === 'body' && data.row.index === 0) {
//         data.cell.styles.fillColor = [232, 245, 255]; // Light blue background
//         data.cell.styles.textColor = 0; // Black text
//       }
      
//       // Text wrapping for employee names
//       if (data.section === 'body' && data.column.index === 0) {
//         const text = data.cell.text[0];
//         if (text && text.length > 25) {
//           data.cell.text = doc.splitTextToSize(text, adjustedWidths[0] - 2);
//         }
//       }
//     },
//     didDrawPage: function (data) {
//       // FOOTER AT THE VERY BOTTOM OF THE PAGE
//       const footerY = pageHeight - 8; // Positioned exactly 8mm from bottom
      
//       // Thin line above footer
//       doc.setDrawColor(220, 220, 220);
//       doc.setLineWidth(0.2);
//       doc.line(margin, footerY - 5, pageWidth - margin, footerY - 5);
      
//       // Footer content - compact
//       doc.setFontSize(7);
//       doc.setTextColor(80);
      
//       // Left: Company name
//       doc.text('AxoPeople', margin, footerY, { align: 'left' });
      
//       // Center: Page number
//       doc.text(`Page ${doc.internal.getCurrentPageInfo().pageNumber}`, pageWidth / 2, footerY, { align: 'center' });
      
//       // Right: Export date
//       doc.text(exportDate, pageWidth - margin, footerY, { align: 'right' });
//     }
//   });

//   doc.save(`leave-balance-${new Date().toISOString().split('T')[0]}.pdf`);
// };

//   const showSnackbar = (message, severity) => {
//     setSnackbar({ open: true, message, severity });
//   };

//   const getBalanceColor = (balance) => {
//     if (balance >= 6) return 'success';
//     if (balance >= 1) return 'warning';
//     return 'error';
//   };

//   if (loading) {
//     return (
//       <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
//         <CircularProgress />
//       </Box>
//     );
//   }

//   // Employee Card View
//   if (IsEmployee) {
//     const leaveData = getEmployeeLeaveData();
//     const employee = employees[0]?.employee;
    
//     return <LeaveBalance
//         employee={employee}
//         leaveData={leaveData}
//         snackbar={snackbar}
//         setSnackbar={setSnackbar}
//       />;

//   }

//   // Admin Table View (unchanged)
//   return (
//     <Box sx={{ p: 3 }} style={{ width: '100%', padding: '0' }}>
//       {/* <PageHeader title={"Leave Balance"} /> */}
//       <Grid container spacing={3}>
//         {/* Filter Controls */}
//         <Grid item xs={12} style={{ width: '100%' }}>
//           <Card sx={{ mb: 2 }}>
//             <CardContent>
//               <Grid container spacing={2} alignItems="center">
//                 <Grid item xs={12} sm={3}>
//                   <TextField
//                     fullWidth
//                     size="small"
//                     label="Search by Name"
//                     variant="outlined"
//                     value={filterName}
//                     onChange={(e) => setFilterName(e.target.value)}
//                   />
//                 </Grid>
//                 <Grid item xs={12} sm={3}>
//                   <FormControl fullWidth size="small" style={{ minWidth: 150 }}>
//                     <InputLabel>Filter by Department</InputLabel>
//                     <Select
//                       value={filterDepartment}
//                       label="Filter by Department"
//                       onChange={(e) => setFilterDepartment(e.target.value)}
//                     >
//                       <MenuItem value="">All Departments</MenuItem>
//                       {departments.map(dept => (
//                         <MenuItem key={dept} value={dept}>{dept}</MenuItem>
//                       ))}
//                     </Select>
//                   </FormControl>
//                 </Grid>
//                 <Grid item xs={12} sm={3}>
//                   <FormControl fullWidth size="small" style={{ minWidth: 150 }}>
//                     <InputLabel>Filter by Position</InputLabel>
//                     <Select
//                       value={filterPosition}
//                       label="Filter by Position"
//                       onChange={(e) => setFilterPosition(e.target.value)}
//                     >
//                       <MenuItem value="">All Positions</MenuItem>
//                       {positions.map(pos => (
//                         <MenuItem key={pos} value={pos}>{pos}</MenuItem>
//                       ))}
//                     </Select>
//                   </FormControl>
//                 </Grid>
//                 <Grid item xs={12} sm={3}>
//                   <Stack direction="row" spacing={1} justifyContent="flex-end">
//                     <Button
//                       variant="outlined"
//                       onClick={() => {
//                         setFilterDepartment('');
//                         setFilterPosition('');
//                         setFilterName('');
//                         setFilterZeroBalances('all');
//                       }}
//                     >
//                       Clear Filters
//                     </Button>
//                     <Button
//                       variant={filterZeroBalances === 'only' ? 'contained' : 'outlined'}
//                       onClick={() => setFilterZeroBalances(filterZeroBalances === 'only' ? 'all' : 'only')}
//                       color="error"
//                     >
//                       Only 0 Balances
//                     </Button>
//                     <Button
//                       variant={filterZeroBalances === 'exclude' ? 'contained' : 'outlined'}
//                       onClick={() => setFilterZeroBalances(filterZeroBalances === 'exclude' ? 'all' : 'exclude')}
//                       color="warning"
//                     >
//                       Exclude 0 Balances
//                     </Button>
//                     <Button
//                       variant="contained"
//                       startIcon={<Update />}
//                       onClick={handleBulkUpdate}
//                       disabled={selected.length === 0}
//                     >
//                       Bulk Update ({selected.length})
//                     </Button>
//                     <Button
//                       variant="contained"
//                       startIcon={<FileDownload />}
//                       onClick={exportToExcel}
//                       color="secondary"
//                     >
//                       Export Excel
//                     </Button>
//                     <Button
//                       variant="contained"
//                       startIcon={<PictureAsPdf />}
//                       onClick={exportToPDF}
//                       color="error"
//                     >
//                       Export PDF
//                     </Button>
//                   </Stack>
//                 </Grid>
//               </Grid>
//             </CardContent>
//           </Card>
//         </Grid>

//         {/* Main Table */}
//         <Grid item xs={12} style={{ width: '100%' }}>
//           <Card>
//             <CardContent sx={{ p: 0 }}>
//               <TableContainer>
//                 <Table>
//                   <TableHead>
//                     <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
//                       <TableCell padding="checkbox">
//                         <Checkbox
//                           indeterminate={selected.length > 0 && selected.length < sortedEmployees.length}
//                           checked={sortedEmployees.length > 0 && selected.length === sortedEmployees.length}
//                           onChange={handleSelectAll}
//                         />
//                       </TableCell>
//                       <TableCell>
//                         <Box display="flex" alignItems="center" onClick={() => handleSort('employee')}>
//                           <strong>Employee</strong>
//                           <IconButton size="small">
//                             <Sort />
//                           </IconButton>
//                         </Box>
//                       </TableCell>
//                       <TableCell>
//                         <Box display="flex" alignItems="center" onClick={() => handleSort('department')}>
//                           <strong>Department</strong>
//                           <IconButton size="small">
//                             <Sort />
//                           </IconButton>
//                         </Box>
//                       </TableCell>
//                       <TableCell>
//                         <Box display="flex" alignItems="center" onClick={() => handleSort('position')}>
//                           <strong>Position</strong>
//                           <IconButton size="small">
//                             <Sort />
//                           </IconButton>
//                         </Box>
//                       </TableCell>
//                       <TableCell>
//                         <Box display="flex" alignItems="center" onClick={() => handleSort('leaveType')}>
//                           <strong>Leave Type</strong>
//                           <IconButton size="small">
//                             <Sort />
//                           </IconButton>
//                         </Box>
//                       </TableCell>
//                       <TableCell>
//                         <Box display="flex" alignItems="center" onClick={() => handleSort('balance')}>
//                           <strong>{previousYear} Balance</strong>
//                           <IconButton size="small">
//                             <Sort />
//                           </IconButton>
//                         </Box>
//                       </TableCell>
//                       <TableCell>
//                         <Box display="flex" alignItems="center" onClick={() => handleSort('balance')}>
//                           <strong>{currentYear} Balance</strong>
//                           <IconButton size="small">
//                             <Sort />
//                           </IconButton>
//                         </Box>
//                       </TableCell>
//                        <TableCell>
//                         <Box display="flex" alignItems="center" onClick={() => handleSort('balance')}>
//                           <strong>Total</strong>
//                           <IconButton size="small">
//                             <Sort />
//                           </IconButton>
//                         </Box>
//                       </TableCell>
//                       <TableCell><strong>Actions</strong></TableCell>
//                     </TableRow>
//                   </TableHead>
//                   <TableBody>
//                     {sortedEmployees.length === 0 ? (
//                       <TableRow>
//                         <TableCell colSpan={8} align="center">
//                           No leave balance records found
//                         </TableCell>
//                       </TableRow>
//                     ) : (
//                       sortedEmployees.map((employee) => (
//                         <TableRow key={employee.id} hover>
//                           <TableCell padding="checkbox">
//                             <Checkbox
//                               checked={selected.includes(employee.id)}
//                               onChange={() => handleSelect(employee.id)}
//                             />
//                           </TableCell>
//                           <TableCell>
//                             {employee.employee 
//                               ? `${employee.employee.firstName || ''} ${employee.employee.lastName || ''}`
//                               : 'Unknown Employee'}
//                           </TableCell>
//                           <TableCell>
//                             <Chip 
//                               label={employee.employee?.department?.name || 'N/A'} 
//                               size="small" 
//                               variant="outlined"
//                             />
//                           </TableCell>
//                           <TableCell>{employee.employee?.position || 'N/A'}</TableCell>
//                           <TableCell>
//                             {/* {editingId === employee.id ? (
//                               <FormControl size="small" sx={{ minWidth: 120 }}>
//                                 <Select
//                                   value={editValues.leaveType}
//                                   onChange={(e) => setEditValues(prev => ({ ...prev, leaveType: e.target.value }))}
//                                 >
//                                   {leaveTypes.map(type => (
//                                     <MenuItem key={type} value={type}>
//                                       {leaveTypeLabels[type]}
//                                     </MenuItem>
//                                   ))}
//                                 </Select>
//                               </FormControl>
//                             ) : ( */}
//                               <Chip 
//                                 label={leaveTypeLabels[employee.leaveType] || employee.leaveType}
//                                 size="small"
//                                 color={employee.leaveType === 'ANNUAL_LEAVE' ? 'primary' : employee.leaveType === 'SICK_LEAVE' ? 'warning' : 'default'}
//                               />
//                             {/* )} */}
//                           </TableCell>
                          
//                           {/* <TableCell>{employee.year}</TableCell> */}
//                           {/* Replace the old Balance and Year cells */}
//                           <TableCell>
//                             {editingId === employee.id ? (
//                               <TextField
//                                 size="small"
//                                 type="number"
//                                 inputProps={{ step: "0.5" }} 
//                                 value={editValues.balance}
//                                 onChange={(e) => {
//                                   const val = e.target.value === '' ? '' : parseFloat(e.target.value);
//                                   setEditValues(prev => ({ ...prev, id:employee.previousId, year:previousYear, balance: isNaN(val) ? '' : val }));
//                                 }}
//                                 sx={{ width: 80 }}
//                               />
//                             ) : (
//                             employee.previousBalance !== undefined ? (
//                               <Chip label={`${employee.previousBalance} days`} size="small" color={getBalanceColor(employee.previousBalance)} />
//                             ) : (
//                               '—'
//                             )
//                            )}
//                           </TableCell>
//                           <TableCell>
//                             {editingId === employee.id ? (
//                               <TextField
//                                 size="small"
//                                 type="number"
//                                 inputProps={{ step: "0.5" }} 
//                                 value={editValues.balance}
//                                 onChange={(e) => {
//                                   const val = e.target.value === '' ? '' : parseFloat(e.target.value);
//                                   setEditValues(prev => ({ ...prev, id: employee.currentId, year:currentYear, balance: isNaN(val) ? '' : val }));
//                                 }}
//                                 sx={{ width: 80 }}
//                               />
//                             ) : (
//                             employee.currentBalance !== undefined ? (
//                               <Chip label={`${employee.currentBalance} days`} size="small" color={getBalanceColor(employee.currentBalance)} />
//                             ) : (
//                               '—'
//                             )
//                           )}
//                           </TableCell>
//                           <TableCell>
//                             <Chip 
//                               label={`${employee.totalBalance} days`}
//                               size="small"
//                               color={getBalanceColor(employee.totalBalance)}
//                             />
//                           </TableCell>
//                           <TableCell>
//                             {editingId === employee.id ? (
//                               <Stack direction="row" spacing={1}>
//                                 <IconButton size="small" color="primary" onClick={handleSave}>
//                                   <Save />
//                                 </IconButton>
//                                 <IconButton size="small" color="secondary" onClick={handleCancel}>
//                                   <Cancel />
//                                 </IconButton>
//                               </Stack>
//                             ) : (
//                               <IconButton size="small" onClick={() => handleEdit(employee)}>
//                                 <Edit />
//                               </IconButton>
//                             )}
//                           </TableCell>
//                         </TableRow>
//                       ))
//                     )}
//                   </TableBody>
//                 </Table>
//               </TableContainer>
//             </CardContent>
//           </Card>
//         </Grid>
//       </Grid>

//       {/* Bulk Update Dialog */}
//       <Dialog open={bulkUpdateDialog} onClose={() => setBulkUpdateDialog(false)} maxWidth="sm" fullWidth>
//         <DialogTitle>Bulk Update Leave Balance</DialogTitle>
//         <DialogContent>
//           <Box sx={{ pt: 2 }}>
//             <Typography variant="body2" sx={{ mb: 2 }}>
//               Update leave balance for {selected.length} selected employee(s)
//             </Typography>
//             <Grid container spacing={2}>
//               <Grid item xs={12}>
//                 <TextField
//                   fullWidth
//                   label="New Balance (days)"
//                   type="number"
//                   value={bulkValues.balance}
//                   onChange={(e) => setBulkValues(prev => ({ ...prev, balance: e.target.value }))}
//                   helperText="Leave empty to keep current balance"
//                 />
//               </Grid>
//               <Grid item xs={12}>
//                 <FormControl fullWidth>
//                   <InputLabel>Leave Type</InputLabel>
//                   <Select
//                     value={bulkValues.leaveType}
//                     label="Leave Type"
//                     onChange={(e) => setBulkValues(prev => ({ ...prev, leaveType: e.target.value }))}
//                   >
//                     {leaveTypes.map(type => (
//                       <MenuItem key={type} value={type}>{leaveTypeLabels[type]}</MenuItem>
//                     ))}
//                   </Select>
//                 </FormControl>
//               </Grid>
//             </Grid>
//           </Box>
//         </DialogContent>
//         <DialogActions>
//           <Button onClick={() => setBulkUpdateDialog(false)}>Cancel</Button>
//           <Button onClick={confirmBulkUpdate} variant="contained">Update</Button>
//         </DialogActions>
//       </Dialog>

//       {/* Snackbar */}
//       <Snackbar
//         open={snackbar.open}
//         autoHideDuration={4000}
//         onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
//       >
//         <Alert severity={snackbar.severity} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}>
//           {snackbar.message}
//         </Alert>
//       </Snackbar>
//     </Box>
//   );
// };

// export default LeaveBalanceManager;
